import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormGroupDirective,
  NgForm,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import {
  debounceTime,
  filter,
  map,
  pairwise,
  Subject,
  Subscription,
  takeUntil,
  tap,
} from "rxjs";
import { ProfileService } from "src/app/services/profile.service";
import { fadeInUp400ms } from "src/@red/animations/fade-in-up.animation";
import { AccessService } from "src/app/services/access.service";
import { ErrorStateMatcher } from "@angular/material/core";
import { DialogConfirmComponent } from "src/@red/components/dialog-confirm/dialog-confirm.component";
import { MatDialog } from "@angular/material/dialog";

export enum Entities {
  INTERLOG = "interlog",
  CLIENT = "client",
  CARRIER = "carrier",
}

interface SelectOption {
  value: string | number;
  label: string;
}

class CrossFieldErrorMatcher implements ErrorStateMatcher {
  constructor(private name: string) {}
  isErrorState(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null
  ): boolean {
    const invalidCtrl = !!(
      control &&
      control.invalid &&
      (control.dirty || control.touched)
    );
    const invalidParent = !!(
      control &&
      (control.parent.hasError("notSame") || control.parent.hasError(this.name))
    );
    return invalidCtrl || invalidParent;
  }
}

@Component({
  selector: "red-profile",
  templateUrl: "./profile.component.html",
  styleUrls: ["./profile.component.scss"],
  animations: [fadeInUp400ms],
})
export class ProfileComponent implements OnInit, OnDestroy {
  @ViewChild("confirmPasswordInput") confirmPasswordInput: ElementRef;

  entities: string[] = [
    Entities.INTERLOG.toUpperCase(),
    Entities.CLIENT.toUpperCase(),
    Entities.CARRIER.toUpperCase(),
  ];

  languages: SelectOption[] = [
    { value: "EN", label: "English" },
    { value: "FR", label: "Français" },
  ];

  dateFormats: SelectOption[] = [
    { value: "dmy", label: "dd/mm/yy - hh:mm:ss" },
    { value: "mdy", label: "mm/dd/yy - hh:mm:ss" },
  ];

  decimalFormats: SelectOption[] = [
    { value: ".", label: $localize`. (dot)` },
    { value: ",", label: $localize`, (comma)` },
  ];

  roles: SelectOption[] = [
    { value: 1, label: "Administrator" },
    { value: 2, label: "Operator" },
    { value: 3, label: "Collaborator" },
  ];

  inputType = "password";
  confirmInputType = "password";
  visible = false;
  confirmVisible = false;

  clients: any[] = [];
  clientsTitle = $localize`Clients`;
  carriers: any[] = [];
  carriersTitle = $localize`Carriers`;

  profileSubscription: Subscription = new Subscription();

  private unsubscribe = new Subject<void>();

  form: FormGroup;
  formPassword: FormGroup;

  userDomains: string[];

  get showDomains() {
    return (
      this.form.get("role").value === 1 &&
      this.form.get("entity").value === "CLIENT"
    );
  }

  constructor(
    private dialog: MatDialog,
    private fb: FormBuilder,
    private profileService: ProfileService,
    private accessService: AccessService
  ) {}

  ngOnDestroy(): void {
    this.unsubscribe.next();
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      email: [{ value: "", disabled: true }, Validators.required],
      name: ["", Validators.required],
      entity: [{ value: "", disabled: true }],
      company: ["", Validators.required],
      language: [""],
      datetime: [""],
      decimal: [""],
      role: [{ value: "", disabled: true }],
    });

    this.formPassword = this.fb.group(
      {
        password: ["", Validators.required],
        confirmPassword: ["", Validators.required],
      },
      { validators: this._checkPasswords }
    );

    this.formPassword.valueChanges.subscribe((values) => {
      if (
        this.formPassword.valid &&
        values.password &&
        values.confirmPassword
      ) {
        // console.log("password form", values);
        this.profileService
          .updateprofile([{ config: "password", new_value: values.password }])
          .subscribe((result) => {
            if (result.result_msg.startsWith("ok")) {
              this.confirmPasswordInput.nativeElement.blur();
              this.formPassword.reset();
            }
          });
      }
    });

    // GET profile data
    const profileData = this.profileService.profileData;

    // Add profile data to form
    this._createAutoSaveForm(profileData);

    // Show domainData if user has permissions
    if (this.showDomains) this.userDomains = profileData.admindomains;

    // GET user client/carrier data
    this.accessService.getByUser
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((data) => {
        // console.log("datadatadatadata", data);

        if (data) {
          this.clients = data.corps;
          this.carriers = data.scacs;
        }
      });
  }

  // show/hide password
  toggleVisibility(field: string) {
    if (field === "password" ? this.visible : this.confirmVisible) {
      field === "password"
        ? (this.inputType = "password")
        : (this.confirmInputType = "password");
      field === "password"
        ? (this.visible = false)
        : (this.confirmVisible = false);
    } else {
      field === "password"
        ? (this.inputType = "text")
        : (this.confirmInputType = "text");
      field === "password"
        ? (this.visible = true)
        : (this.confirmVisible = true);
    }
  }

  errorMatcher(name: string) {
    return new CrossFieldErrorMatcher(name);
  }

  private _checkPasswords: ValidatorFn = (
    group: AbstractControl
  ): ValidationErrors | null => {
    let pass = group.get("password").value;
    let confirmPass = group.get("confirmPassword").value;
    return pass === confirmPass ? null : { notSame: true };
  };

  private _createAutoSaveForm(data) {
    this.form.valueChanges
      .pipe(
        debounceTime(500),
        pairwise(),
        map(([oldState, newState]) => {
          let changes = {};
          for (const key in newState) {
            if (
              (oldState[key] !== newState[key] &&
                oldState[key] !== undefined) ||
              this.form.controls[key].dirty
            ) {
              changes[key] = newState[key];
            }
          }
          return changes;
        }),
        tap(() => {
          if (this.form.invalid) {
            this._validateAllFormFields(this.form);
          }
        }),
        filter(
          (changes) => Object.keys(changes).length !== 0 && !this.form.invalid
        ),
        takeUntil(this.unsubscribe)
      )
      .subscribe((values) => {
        const data = Object.entries(values).map((value) => {
          return { config: value[0], new_value: value[1] };
        });

        this.profileService.updateprofile(data).subscribe((result) => {
          if (result.result_msg.startsWith("ok")) {
            this.form.get(Object.keys(values)).markAsPristine();
          }

          // Ask for reload if language changed
          if (Object.keys(values).includes("language")) {
            this._confirmReloadForLanguageChange(
              values["language"].toLowerCase()
            );
          }
        });
      });

    this.form.patchValue(data);
  }

  private _validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this._validateAllFormFields(control);
      }
    });
  }

  private _confirmReloadForLanguageChange(language: string) {
    this.dialog
      .open(DialogConfirmComponent, {
        data: {
          title: $localize`You changed your language!`,
          content: $localize`Do you want to reload and take effect immediately?`,
          data: language,
        },
        width: "420px",
      })
      .afterClosed()
      .subscribe((language) => {
        if (language) {
          window.location.href = `/${language}/profile`;
        }
      });
  }
}
